(() => {
  function onReady(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  function urlLooksSuccessful(url) {
    try {
      const u = new URL(url, window.location.origin);
      const sp = u.searchParams;
      return sp.get('customer_posted') === 'true' || sp.get('contact_posted') === 'true';
    } catch {
      return false;
    }
  }

  function pageUrlLooksSuccessful() {
    return urlLooksSuccessful(window.location.href);
  }

  function setHidden(el, hidden) {
    if (!el) return;
    if (hidden) el.setAttribute('hidden', '');
    else el.removeAttribute('hidden');

    // Webflow success/error blocks are typically `display:none` by default in CSS,
    // so toggling the `hidden` attribute alone isn't sufficient.
    el.style.display = hidden ? 'none' : 'block';
  }

  function setSubmitState(submitEl, disabled, label) {
    if (!submitEl) return;
    submitEl.disabled = !!disabled;
    if (label == null) return;

    if (submitEl.tagName === 'INPUT') submitEl.value = label;
    else submitEl.textContent = label;
  }

  function getSubmitLabel(submitEl) {
    if (!submitEl) return '';
    return submitEl.tagName === 'INPUT' ? submitEl.value : submitEl.textContent;
  }

  function extractResultFromHtml(html) {
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const returnedForm =
        doc.querySelector('form#contact_form') ||
        doc.querySelector('form.footer-newsletter-form') ||
        doc.querySelector('form[action*="/contact"]');
      if (!returnedForm) return { ok: false };

      const returnedSuccess =
        returnedForm.querySelector('.success-message-3.w-form-done') ||
        returnedForm.querySelector('.form-success') ||
        returnedForm.querySelector('[data-form-success]');

      const returnedFail =
        returnedForm.querySelector('.w-form-fail') ||
        returnedForm.querySelector('.errors') ||
        returnedForm.querySelector('[data-form-error]');

      const successShown =
        !!returnedSuccess &&
        !returnedSuccess.hasAttribute('hidden') &&
        String(returnedSuccess.style.display || '').toLowerCase() !== 'none';

      const failShown =
        !!returnedFail &&
        !returnedFail.hasAttribute('hidden') &&
        String(returnedFail.style.display || '').toLowerCase() !== 'none';

      if (successShown && !failShown) return { ok: true };
      if (failShown) return { ok: false, errorText: (returnedFail.textContent || '').trim() };
      return { ok: false };
    } catch {
      return { ok: false };
    }
  }

  function initForm(form) {
    const emailInput = form.querySelector('input[type="email"]');
    const submitEl = form.querySelector('input[type="submit"], button[type="submit"]');
    const successEl = form.querySelector('.success-message-3.w-form-done');
    const failEl = form.querySelector('.w-form-fail');

    if (!emailInput || !submitEl || !successEl || !failEl) return;

    const defaultLabel = getSubmitLabel(submitEl);
    const thanksLabel = submitEl.getAttribute('data-success-label') || 'Thanks for subscribing';

    // Show submit only once the user starts typing an email (also handles autofill/backspace).
    const updateTypingState = () => {
      const hasEmail = String(emailInput.value || '').trim().length > 0;
      form.classList.toggle('has-email', hasEmail);
      // If the user starts typing again after a successful subscription, reset the "thanks" state.
      if (hasEmail && form.classList.contains('is-subscribed')) {
        form.classList.remove('is-subscribed');
        setSubmitState(submitEl, false, defaultLabel);
      }
    };
    emailInput.addEventListener('input', updateTypingState);
    emailInput.addEventListener('change', updateTypingState);
    updateTypingState();

    // If we're not on the same origin as the form action, don't hijack submit.
    // Cross-origin `fetch()` will typically be blocked by CORS, but a normal form POST is allowed.
    let actionUrl = null;
    try {
      const raw = form.getAttribute('action') || window.location.href;
      actionUrl = new URL(raw, window.location.origin);
    } catch {
      actionUrl = null;
    }

    const host = String(window.location.hostname || '').toLowerCase();
    const isLocalhost = host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0';
    const isSameOrigin = actionUrl ? actionUrl.origin === window.location.origin : true;

    // If the page reloaded after a successful subscribe (Shopify redirect sets query params),
    // show the success state anyway.
    if (pageUrlLooksSuccessful()) {
      form.classList.add('is-subscribed');
      form.classList.remove('has-email');
      setSubmitState(submitEl, true, thanksLabel);
      // We use the submit label ("Thanks for subscribing") as the confirmation UI.
      // Keep the Webflow success block hidden to avoid duplicate messaging.
      setHidden(successEl, true);
      setHidden(failEl, true);
    }

    // Shopify's `/contact` endpoint may not behave like production when previewing via localhost.
    // Let the browser do a normal form submit in that case.
    if (isLocalhost) return;

    // If cross-origin, don't hijack submit (let browser do normal POST).
    if (!isSameOrigin) return;

    // Capture-phase listener so we can prevent other handlers (e.g. Webflow) from triggering reload.
    form.addEventListener(
      'submit',
      async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();

        // Reset messages
        setHidden(successEl, true);
        setHidden(failEl, true);

        const waitLabel = submitEl.getAttribute('data-wait') || 'Please wait...';
        setSubmitState(submitEl, true, waitLabel);

        try {
          // Shopify's `/contact` endpoint expects standard browser form encoding
          // (application/x-www-form-urlencoded), not multipart/form-data.
          const fd = new FormData(form);
          const body = new URLSearchParams();
          for (const [k, v] of fd.entries()) {
            body.append(k, typeof v === 'string' ? v : '');
          }
          const action = actionUrl ? actionUrl.toString() : form.getAttribute('action') || window.location.href;

          const res = await fetch(action, {
            method: 'POST',
            body,
            credentials: 'same-origin',
            redirect: 'follow',
            headers: {
              Accept: 'text/html',
              'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
            },
          });

          // In theme preview, Shopify can respond with 4xx for XHR even though a normal POST works.
          // If we get a non-2xx response, fall back to a standard browser submit so the email is added.
          if (!res.ok) {
            throw new Error(`Newsletter subscribe failed: HTTP ${res.status}`);
          }

          const html = await res.text();
          const parsed = extractResultFromHtml(html);
        // Shopify typically redirects on success; in some theme preview contexts the success
        // params may not be present, so treat "redirected + no explicit error" as success.
        const ok = parsed.ok || urlLooksSuccessful(res.url) || (res.redirected && !parsed.errorText);

          if (ok) {
            emailInput.value = '';
            form.classList.add('is-subscribed');
            form.classList.remove('has-email');
            setSubmitState(submitEl, true, thanksLabel);
            // We use the submit label ("Thanks for subscribing") as the confirmation UI.
            // Keep the Webflow success block hidden to avoid duplicate messaging.
            setHidden(successEl, true);
          } else {
          const errorText = (parsed.errorText || '').replace(/\s+/g, ' ').trim();
            if (errorText) {
              const box = failEl.querySelector('div') || failEl;
              box.textContent = errorText;
            }
            setHidden(failEl, false);
          }
        } catch {
          // If fetch fails for any reason, fall back to a normal submit (page reload).
          // This keeps the form working in environments where /contact can't be fetched.
          setSubmitState(submitEl, false, defaultLabel);
          try {
            form.submit();
          } catch {
            // last resort: show failure state
            setHidden(failEl, false);
          }
          return;
        } finally {
          // Only restore the button state on failure; success uses the "thanks" label + disabled state.
          if (!form.classList.contains('is-subscribed')) {
            setSubmitState(submitEl, false, defaultLabel);
          }
        }
      },
      true,
    );
  }

  onReady(() => {
    document.querySelectorAll('form.footer-newsletter-form').forEach(initForm);
  });
})();


